import {
    AlertFilled,
    AlertOffFilled,
    AlertOffRegular,
    AlertRegular,
    ArrowSyncRegular,
    bundleIcon,
    PremiumFilled,
    PremiumRegular,
    RewardRegular,
    SettingsRegular,
    ArrowSyncFilled,
    SettingsFilled,
} from '@fluentui/react-icons';
import {
    CalendarTodoRegular,
    CalendarTodoFilled,
    TeamsRegular,
    TeamsFilled,
} from '@fluentui/react-brand-icons';
import getGlobalSettingsAccountMailboxInfo from 'owa-account-source-list-store/lib/selectors/getGlobalSettingsAccountMailboxInfo';
import { getUnseenItemsCount as getUnseenNotificationsCount } from 'owa-activity-feed/lib/store/unseenItemsSelector';
import { logUsage } from 'owa-analytics';
import getDrawerPaneId from 'owa-app-pane-group-utils/lib/getDrawerPaneId';
import { isCapabilityEnabled } from 'owa-capabilities';
import { rewardsBadgeCapability } from 'owa-capabilities-definitions/lib/rewardsBadgeCapability';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { OwaHeaderButtonControls } from 'owa-header-types';
import loc from 'owa-localize';
import { lazyManualSyncMail } from 'owa-mail-manual-sync';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import areNotificationsDisabled from 'owa-notifications-settings/lib/selectors/areNotificationsDisabled';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { isPremiumConsumer } from 'owa-session-store';
import { getUnreadConversationsCount as getTeamsUnreadConversationsCount } from 'owa-teams-embed-client-store';
import { isTimePanelAvailable } from 'owa-time-panel-bootstrap';
import { lazyOpenPremiumDashboard } from 'owa-whats-new';
import {
    MY_DAY_BUTTON_ID,
    NOTIFICATIONS_BUTTON_ID,
    PREMIUM_BUTTON_ID,
    REWARDS_BUTTON_ID,
    SETTINGS_BUTTON_ID,
    TEAMS_BUTTON_ID,
    SYNC_BUTTON_ID,
} from '../constants/constants';
import onMyDayButtonClicked from '../utils/onMyDayButtonClicked';
import isTeamsChatButtonEnabled from '../utils/isTeamsChatButtonEnabled';
import { MyDayPane, NotificationsPane, TeamsChatPane } from 'owa-header-panes';
import {
    myDayHeaderButton,
    notificationsDisabledHeaderButton,
    notificationsHeaderButton,
    notificationsHeaderButtonWithCount,
    premiumHeaderButton,
    rewardsHeaderButton,
    settingsHeaderButton,
    syncHeaderButton,
    syncHeaderButtonTooltip,
    teamsChatHeaderButton,
    teamsChatHeaderButtonWithCount,
} from './OwaHeaderButton.locstring.json';
import { brandIcon } from './headerButtons.scss';

const TeamsIcon = bundleIcon(TeamsFilled, TeamsRegular);
const MyDayIcon = bundleIcon(CalendarTodoFilled, CalendarTodoRegular);
const SyncIcon = bundleIcon(ArrowSyncFilled, ArrowSyncRegular);
const SettingsIcon = bundleIcon(SettingsFilled, SettingsRegular);
const PremiumIcon = bundleIcon(PremiumFilled, PremiumRegular);

const NotificationsIcon = bundleIcon(AlertFilled, AlertRegular);
const NotificationsOffIcon = bundleIcon(AlertOffFilled, AlertOffRegular);

/*
 * The order of the buttons in this array is important.
 * The buttons are rendered in the order they are defined here from left to right.
 */
export const headerButtons: OwaHeaderButtonControls = [
    {
        id: PREMIUM_BUTTON_ID,
        renderLabel: () => loc(premiumHeaderButton),
        renderIcon: () => <PremiumIcon />,
        onClick: () => {
            logUsage(
                'HeaderPremiumDiamondClicked',
                getGlobalSettingsAccountMailboxInfo(),
                undefined
            );
            lazyOpenPremiumDashboard.importAndExecute();
        },
        isVisible: () => isPremiumConsumer(getGlobalSettingsAccountMailboxInfo()),
        overflowItem: {
            priority: 2,
            divider: 'after',
        },
    },
    {
        id: TEAMS_BUTTON_ID,
        renderLabel: () => loc(teamsChatHeaderButton),
        renderIcon: () => <TeamsIcon className={brandIcon} />,
        isVisible: () => isTeamsChatButtonEnabled(),
        pane: {
            id: getDrawerPaneId('teamsChat'),
            render: id => <TeamsChatPane paneId={id} />,
        },
        badge: {
            getCount: () => {
                return getTeamsUnreadConversationsCount();
            },
        },
        renderTooltip: () => {
            const unreadCount = getTeamsUnreadConversationsCount();
            return unreadCount > 0
                ? loc(teamsChatHeaderButtonWithCount, unreadCount)
                : loc(teamsChatHeaderButton);
        },
        overflowItem: {
            priority: 1,
        },
    },
    {
        id: MY_DAY_BUTTON_ID,
        renderLabel: () => loc(myDayHeaderButton),
        renderIcon: () => <MyDayIcon />,
        onClick: () => onMyDayButtonClicked(),
        isVisible: () => isTimePanelAvailable(),
        pane: {
            id: getDrawerPaneId('myDay'),
            render: id => <MyDayPane paneId={id} />,
        },
        overflowItem: {
            priority: 3,
        },
    },
    {
        id: REWARDS_BUTTON_ID,
        renderLabel: () => loc(rewardsHeaderButton),
        renderIcon: () => <RewardRegular />,
        // Reads the currently-viewed (module context) account. Visibility re-evaluates live on an
        // in-app account switch because useHeaderButtonVisibility runs isVisible() inside a MobX
        // `computed` within the observer OwaHeaderButtonGroup, so this observable access is tracked.
        isVisible: () => isCapabilityEnabled(rewardsBadgeCapability, getModuleContextMailboxInfo()),
        renderTooltip: () => loc(rewardsHeaderButton),
        // Pin Rewards next to the bell so it stays visible (out of the overflow menu) for the current
        // account when its capability is enabled.
        overflowItem: {
            priority: 6,
            addsHeaderSlot: true,
        },
    },
    {
        id: NOTIFICATIONS_BUTTON_ID,
        renderLabel: () => loc(notificationsHeaderButton),
        renderIcon: () =>
            // Show unmuted alert icon if notifications are enabled or are loading
            areNotificationsDisabled() ? <NotificationsOffIcon /> : <NotificationsIcon />,
        pane: {
            id: getDrawerPaneId('notifications'),
            render: id => <NotificationsPane paneId={id} />,
        },
        badge: {
            getCount: () => getUnseenNotificationsCount(),
        },
        renderTooltip: () => {
            if (areNotificationsDisabled()) {
                return loc(notificationsDisabledHeaderButton);
            }
            const unseenCount = getUnseenNotificationsCount();
            return unseenCount > 0
                ? loc(notificationsHeaderButtonWithCount, unseenCount)
                : loc(notificationsHeaderButton);
        },
        overflowItem: {
            priority: 5,
        },
    },
    {
        id: SYNC_BUTTON_ID,
        renderLabel: () => loc(syncHeaderButton),
        renderIcon: () => <SyncIcon />,
        onClick: () => {
            logUsage('SyncButtonClicked', getGlobalSettingsAccountMailboxInfo(), {
                source: 'Header',
            });
            lazyManualSyncMail.importAndExecute(getSelectedTableView());
        },
        isVisible: () => isGlobalFeatureEnabled('fwk-header-sync-button-and-cleanup'),
        renderTooltip: () => loc(syncHeaderButtonTooltip),
        overflowItem: {
            priority: 0,
        },
    },
    {
        id: SETTINGS_BUTTON_ID,
        renderLabel: () => loc(settingsHeaderButton),
        renderIcon: () => <SettingsIcon />,
        onClick: () => lazyMountAndShowFullOptions.importAndExecute(),
        overflowItem: {
            priority: 4,
            divider: 'before',
        },
    },
] as const;
